import { boardMode, marketKey } from "./derive";
import { compareEntities } from "./store";
import {
  IN_PLAY, ladderLineId,
  type BoardRow, type BoardView, type BookCell, type BookId, type Entity, type EntityKey, type EventHeader, type EventId, type FamilyId, type InplayCell, type InplayEvent, type InplayGame, type InplayList,
  type InplayStatus, type Ladder, type LadderLine, type LadderLineId, type LadderOutcome, type LeagueSummary, type MarketQuote, type MarketSpec, type RowId,
} from "./types";

export const INPLAY_EVENT_TOPICS = 14;

const NO_GAMES: readonly InplayGame[] = [];
const NO_DEPTH: LadderOutcome["depth"] = [];

export function inplayRowId(event: EventId, entity: EntityKey = ""): RowId {
  return `${event}|${entity}`;
}

export function splitRowId(rowId: RowId): { readonly event: EventId; readonly entity: EntityKey } {
  const index = rowId.indexOf("|");
  return index === -1 ? { event: rowId, entity: "" } : { event: rowId.slice(0, index), entity: rowId.slice(index + 1) };
}

export function ladderEventOf(event: EventHeader | undefined): EventId {
  return event === undefined || event.live ? "" : event.id;
}

export function inplayCarries(game: InplayGame | undefined, family: FamilyId, entity: EntityKey): boolean {
  return game !== undefined && (entity === "" ? game.gameFamilies : game.families).includes(family);
}

export function inplayGames(list: InplayList | null, family: FamilyId): readonly InplayGame[] {
  if (list === null) return NO_GAMES;
  return [...list.games.values()]
    .filter((game) => game.gameFamilies.includes(family))
    .sort((a, b) => a.startMs - b.startMs || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export function inplayFamilyCounts(list: InplayList | null): string {
  if (list === null) return "";
  const counts = new Map<FamilyId, number>();
  for (const game of list.games.values()) {
    for (const family of game.gameFamilies) counts.set(family, (counts.get(family) ?? 0) + 1);
  }
  return [...counts].sort(([a], [b]) => (a < b ? -1 : 1)).map(([family, count]) => `${family}:${count}`).join(",");
}

export function withInplayFamilies(league: LeagueSummary | undefined, counts: string): LeagueSummary | undefined {
  if (league === undefined || counts === "") return league;
  const live = new Map(counts.split(",").map((entry): [FamilyId, number] => {
    const at = entry.lastIndexOf(":");
    return [entry.slice(0, at), Number(entry.slice(at + 1))];
  }));
  return {
    ...league,
    families: league.families.map((family) => {
      const games = boardMode(family) === "lines" ? live.get(family.id) ?? 0 : 0;
      return games === 0 ? family : { ...family, events: family.events + games, rows: family.rows + games };
    }),
  };
}

const headers = new WeakMap<InplayGame, EventHeader>();
const propsHeaders = new WeakMap<InplayGame, EventHeader>();

function headerOf(game: InplayGame, held: WeakMap<InplayGame, EventHeader>, liveProps: boolean): EventHeader {
  let header = held.get(game);
  if (header === undefined) {
    header = {
      id: game.id, name: game.name, startMs: game.startMs, home: game.home, away: game.away,
      homeShort: game.homeShort, awayShort: game.awayShort, homeLogoUrl: game.homeLogoUrl, awayLogoUrl: game.awayLogoUrl, props: 0, live: game.status ?? IN_PLAY,
      ...(liveProps ? { liveProps } : {}),
    };
    held.set(game, header);
  }
  return header;
}

export function inplayHeader(game: InplayGame): EventHeader {
  return headerOf(game, headers, false);
}

export function inplayEvent(list: InplayList | null, id: EventId): EventHeader | undefined {
  const game = list?.games.get(id);
  return game === undefined ? undefined : inplayHeader(game);
}

export function withInplay(view: BoardView, games: readonly InplayGame[], ended: ReadonlySet<EventId>, propsFamilies: ReadonlySet<FamilyId>): BoardView {
  if (games.length === 0 && ended.size === 0) return view;
  const events = new Map(view.events);
  for (const id of ended) events.delete(id);
  const rows = new Map(view.rows);
  const live = new Set<RowId>();
  for (const game of games) {
    const rowId = inplayRowId(game.id);
    events.set(game.id, game.families.some((family) => propsFamilies.has(family)) ? headerOf(game, propsHeaders, true) : inplayHeader(game));
    rows.set(rowId, { eventId: game.id, entity: null });
    live.add(rowId);
  }
  const pregame = view.rowIds.filter((rowId) => !live.has(rowId) && !ended.has(view.rows.get(rowId)?.eventId ?? ""));
  return { ...view, events, rows, rowIds: [...live, ...pregame] };
}

export function withGameStates<V extends { readonly events: ReadonlyMap<EventId, EventHeader> }>(view: V, statuses: ReadonlyMap<EventId, InplayStatus>): V {
  let events: Map<EventId, EventHeader> | null = null;
  for (const [id, event] of view.events) {
    const live = statuses.get(id);
    if (live === undefined) continue;
    events ??= new Map(view.events);
    events.set(id, { ...event, live });
  }
  return events === null ? view : { ...view, events };
}

type Placed = { readonly cell: InplayCell; readonly outcome: number; readonly line: number };

function paused(cell: InplayCell): boolean {
  return cell.suspended || cell.stale;
}

type Subject = { readonly entity: Entity | null; readonly placed: readonly (readonly Placed[])[] };

type Partition = {
  readonly specs: readonly MarketSpec[];
  readonly subjects: ReadonlyMap<EntityKey, Subject>;
  rows: { readonly event: EventId; readonly map: ReadonlyMap<RowId, BoardRow> } | null;
};

const partitions = new WeakMap<InplayEvent, Partition>();

function partition(event: InplayEvent, specs: readonly MarketSpec[]): Partition {
  const held = partitions.get(event);
  if (held?.specs === specs) return held;
  const slots = new Map<string, number>();
  specs.forEach((spec, slot) => { for (const key of spec.keys) slots.set(key, slot); });
  const grouped = new Map<EntityKey, { named: InplayCell; readonly placed: Placed[][] }>();
  for (const cell of event.cells.values()) {
    if (!(cell.odds > 1)) continue;
    const slot = slots.get(marketKey(cell.market));
    if (slot === undefined) continue;
    const spec = specs[slot];
    const side = cell.side.toLowerCase();
    const outcome = spec.outcomes.indexOf(side);
    if (outcome === -1) continue;
    const line = spec.line === "none" ? 0 : cell.line === null ? null : spec.line === "spread" && side === "away" ? 0 - cell.line : cell.line;
    if (line === null) continue;
    const key = cell.entity?.key ?? "";
    let subject = grouped.get(key);
    if (subject === undefined) {
      subject = { named: cell, placed: specs.map(() => []) };
      grouped.set(key, subject);
    } else if (cell.id < subject.named.id) {
      subject.named = cell;
    }
    subject.placed[slot].push({ cell, outcome, line });
  }
  const subjects = [...grouped].map(([key, { named, placed }]): [EntityKey, Subject] => [key, { entity: named.entity, placed }]);
  const built: Partition = { specs, subjects: new Map(subjects.sort(([, a], [, b]) => compareEntities(a.entity, b.entity))), rows: null };
  partitions.set(event, built);
  return built;
}

function fresh(placed: readonly Placed[]): readonly Placed[] {
  const live = placed.filter((entry) => !entry.cell.stale);
  return live.length > 0 ? live : placed;
}

function mainLine(placed: readonly Placed[]): number | null {
  const pool = fresh(placed);
  const votes = new Map<number, Set<BookId>>();
  const vote = (entry: Placed) => {
    const books = votes.get(entry.line) ?? new Set<BookId>();
    books.add(entry.cell.book);
    votes.set(entry.line, books);
  };
  for (const entry of pool) if (!entry.cell.alt && !paused(entry.cell)) vote(entry);
  if (votes.size === 0) for (const entry of pool) if (!entry.cell.alt) vote(entry);
  if (votes.size === 0) for (const entry of pool) vote(entry);
  let main: number | null = null;
  let most = 0;
  for (const [line, books] of votes) {
    const closer = main !== null && (Math.abs(line) < Math.abs(main) || (Math.abs(line) === Math.abs(main) && line < main));
    if (books.size > most || (books.size === most && closer)) {
      main = line;
      most = books.size;
    }
  }
  return main;
}

function bookLine(cells: readonly Placed[], target: number): number {
  if (cells.some((entry) => entry.line === target)) return target;
  const own = cells.filter((entry) => !entry.cell.alt);
  return (own.length > 0 ? own : cells).reduce((best, entry) => {
    const gap = Math.abs(entry.line - target) - Math.abs(best - target);
    return gap < 0 || (gap === 0 && entry.line < best) ? entry.line : best;
  }, (own[0] ?? cells[0]).line);
}

function byBook(placed: readonly Placed[]): readonly (readonly [BookId, readonly Placed[]])[] {
  const books = new Map<BookId, Placed[]>();
  for (const entry of placed) {
    const cells = books.get(entry.cell.book);
    if (cells === undefined) books.set(entry.cell.book, [entry]);
    else cells.push(entry);
  }
  return [...books].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

function quote(placed: readonly Placed[], spec: MarketSpec): MarketQuote | null {
  if (placed.length === 0) return null;
  const main = spec.line === "none" ? null : mainLine(placed);
  const target = main ?? 0;
  const books = byBook(placed).map(([book, all]): BookCell => {
    const line = bookLine(fresh(all), target);
    const at = spec.outcomes.map((_, outcome) => fresh(all.filter((entry) => entry.line === line && entry.outcome === outcome))[0]?.cell);
    const odds = at.map((cell) => cell?.odds ?? null);
    const held = at.map((cell) => cell !== undefined && paused(cell));
    return line === target ? { at: "main", book, odds, stakes: null, held } : { at: "off", book, line, odds, stakes: null, held };
  });
  return { main, lines: new Set(placed.map((entry) => entry.line)).size, books };
}

export function inplayRows(event: EventId, cells: InplayEvent, specs: readonly MarketSpec[]): ReadonlyMap<RowId, BoardRow> {
  const held = partition(cells, specs);
  if (held.rows?.event === event) return held.rows.map;
  const map = new Map<RowId, BoardRow>();
  for (const [key, { entity, placed }] of held.subjects) {
    const markets = specs.map((spec, slot) => quote(placed[slot], spec));
    if (markets.every((market) => market === null)) continue;
    const id = inplayRowId(event, key);
    map.set(id, { id, eventId: event, entity, player: null, markets });
  }
  held.rows = { event, map };
  return map;
}

export function inplayLadder(cells: InplayEvent, specs: readonly MarketSpec[], entity: EntityKey): Ladder {
  const placed = partition(cells, specs).subjects.get(entity)?.placed ?? specs.map((): Placed[] => []);
  const lines = new Map<LadderLineId, LadderLine>();
  placed.forEach((entries, slot) => {
    const values = [...new Set(entries.map((entry) => entry.line))];
    for (const line of values) {
      const books = byBook(entries.filter((entry) => entry.line === line)).map(([book, at]) => ({
        book,
        outcomes: specs[slot].outcomes.map((_, outcome): LadderOutcome | null => {
          const cell = fresh(at.filter((entry) => entry.outcome === outcome))[0]?.cell;
          return cell === undefined ? null : { odds: cell.odds, stake: null, depth: NO_DEPTH, link: cell.link, appLink: cell.appLink, selectionId: null, side: null, held: paused(cell) };
        }),
      }));
      lines.set(ladderLineId(slot, line), { slot, line, books });
    }
  });
  return { mains: specs.map((spec, slot) => (spec.line === "none" ? null : mainLine(placed[slot]))), lines };
}

export type Slots = {
  acquire(id: string): () => void;
  granted(id: string): boolean;
  listen(onChange: () => void): () => void;
};

export function createSlots(cap: number): Slots {
  const refs = new Map<string, number>();
  const listeners = new Set<() => void>();
  const notify = () => { for (const listener of [...listeners]) listener(); };
  return {
    acquire(id) {
      const held = refs.get(id) ?? 0;
      refs.set(id, held + 1);
      if (held === 0) notify();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const left = (refs.get(id) ?? 1) - 1;
        if (left > 0) {
          refs.set(id, left);
          return;
        }
        refs.delete(id);
        notify();
      };
    },
    granted(id) {
      let rank = 0;
      for (const key of refs.keys()) {
        if (key === id) return true;
        rank += 1;
        if (rank >= cap) return false;
      }
      return false;
    },
    listen(onChange) {
      listeners.add(onChange);
      return () => { listeners.delete(onChange); };
    },
  };
}
